<template>
  <communication-item
    :type="type"
    :title="title"
    :date="date"
    :icon="icon"
    color="is-calls"
  >
    <template v-slot:subtext>
      <app-debugging :info="call"></app-debugging>
      <p>{{ duration }} <span v-if="recordingUrlMessage" class="ml-50">{{ recordingUrlMessage }}</span></p>

      <p v-if="recordingURL">
        <template v-if="call.type === ENUMS.CommunicationTypeEnum.VideoCall.value">
          <video width="320" height="240" controls>
            <source :src="recordingURL" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </template>
        <template v-else>
          <audio controls>
            <source :src="recordingURL">
          </audio>
        </template>
      </p>

      <p v-if="call.recording_url">
        <button-link
          @click="getRecording"
          :loading="recordingLoading"
        >listen to {{ call.has_voicemail ? "voicemail" : "recording" }}</button-link>
      </p>
      <p v-else>
        <button-link
          v-if="! recordingUrlMessage"
          @click="getRecording"
          :loading="recordingLoading"
        >check for recording</button-link>
      </p>

      <template v-if="isSmrtPhone">
        <template v-if="showCallId">
          <p>
            <button-link
              @click="showCallId = false"
            >hide call sid</button-link>

            <button-link
              @click="copyCallSid(call.callrail_id)"
              size="is-small"
            >
              {{ call.callrail_id }}
              <icon-default
                class="copy_icon ml-25"
                icon="copy"
                size="is-small"
              />
            </button-link>
          </p>
        </template>
        <template v-else>
          <p>
            <button-link
              @click="showCallId = true"
            >show call sid</button-link>
          </p>
        </template>
      </template>

      <p v-if="createdBy">{{ createdBy }}</p>
    </template>

    <b-checkbox
      v-if="isInbound"
      size="is-small"
      @input="handleCheck($event, call.id)"
      type="is-success"
      :value="isAddressed"
      :disabled="isDisabled"
    >
      <template v-if="call.addressed_at">
        Addressed by <strong>{{ addressedByName }}</strong> on <strong>{{ addressedDate }}</strong>
      </template>
      <template v-else>Not addressed</template>
    </b-checkbox>

    <template v-slot:column>
      <slot name="column"></slot>
    </template>
  </communication-item>
</template>

<script>
import CommunicationItem from "./CommunicationItem";
import {ENUMS} from "../../../services/enums";
import dates from "../../../services/dates";
import LocalStorage from "../../../services/localStorage";
import AppDebugging from "../../../components/app/AppDebugging.vue";

export default {
  name: "CommunicationItemCall",
  components: {AppDebugging, CommunicationItem},
  props: {
    call: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isDisabled: false,
      recordingURL: null,
      recordingUrlMessage: null,
      recordingLoading: false,
      showCallId: false,
    }
  },
  computed: {
    isVideoCall() {
      return this.call.type === ENUMS.CommunicationTypeEnum.VideoCall.value;
    },
    isAddressed() {
      return !!this.call.addressed_at;
    },
    addressedByName() {
      return this.$store.getters.users.find(user => user.id === this.call.addressed_by).full_name;
    },
    addressedDate() {
      return dates.formatAsDateTime(this.call.addressed_at);
    },
    isInbound() {
      return this.call.communication_direction === ENUMS.CommunicationDirectionEnum.Inbound.value;
    },
    type() {
      let suffix = ' Call';

      if(this.isVideoCall) {
        suffix = ' Video Call';
      }

      if(this.isInbound) {
        if(this.call.was_answered) {
          return 'Inbound' + suffix;
        } else {
          return 'Missed' + suffix;
        }
      } else {
        return 'Outbound' + suffix;
      }
    },
    title() {
      const contact = this.call.contact_number;
      const number = this.call.integration_label;

      if(this.isInbound) {
        return contact + ' > ' + number;
      } else {
        return number + ' > ' + contact;
      }
    },
    date() {
      if (this.call.scheduled_at) {
        return dates.formatAsDateTime(this.call.scheduled_at);
      }

      return dates.formatAsDateTime(this.call.created_at);
    },
    duration() {
      return this.$utils.formatDuration(this.call.duration_in_seconds);
    },
    createdBy() {
      if (! this.call.createdBy.display_name) return null;

      if (this.call.communication_direction === ENUMS.CommunicationDirectionEnum.Outbound.value) {
        return `by ${this.call.createdBy.display_name}`;
      }

      return null;
    },
    icon() {
      if(this.isInbound && !this.call.was_answered) {
        return 'call-missed';
      } else {
        return 'call';
      }
    },
    isSmrtPhone() {
      if (! this.$store.getters.isSmrtPhoneIntegrationComplete) return false;

      const phone = this.$store.getters.integrationPhoneNumbers.find(phone => phone.id === this.call.integration_phone_number_id);

      if (! phone) return false;

      return this.call.callrail_id && phone.integration_id === this.$store.getters.smrtPhoneIntegration.id
    },
  },
  methods: {
    getRecording() {
      this.recordingLoading = true;
      this.$forceUpdate();

      this.$api.callRailIntegrations.getRecording(this.call.id).then(res => {
        if (res.data.url) {
          this.recordingURL = res.data.url;
        } else {
          this.recordingUrlMessage = 'no recording'
        }

        this.recordingLoading = false;
        this.$forceUpdate();
      });
    },
    handleCheck(val, id) {
      const addressedBy = val ? this.$store.state.activeUser : null;

      this.isDisabled = true;

      this.$api.communicationLogs.setAddressed({ id, addressedBy }).then(() => {
        setTimeout(() => {
          this.isDisabled = false;
        }, 1000);
      });
    },
    copyCallSid(callSid) {
      this.$utils.copyToClipboard(callSid);
    },
  }
}
</script>
